import React, { useState, useEffect } from "react";
import api from "../../api/api";

// ── Telegram Notify Card ─────────────────────────────────────────────────────
export function TelegramNotifyCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [branding, setBranding] = useState(true);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const load = async () => {
    try {
      const d = await api.getTelegramSettings();
      setChatId(d.chatId || "");
      setEnabled(!!d.enabled);
      setBranding(d.brandingEnabled !== false);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      await api.updateTelegramSettings({ chatId: chatId.trim(), enabled, brandingEnabled: branding });
      setMsg("Saved");
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const cardStyle = {
    flex: 1, maxWidth: 420, padding: 20,
    background: "var(--bg-surface)", border: "1px solid var(--border)",
    borderRadius: "var(--radius-xl)",
  };

  if (loading) {
    return <div style={{ ...cardStyle, color: "var(--text-muted)", fontSize: 13 }}>Loading Telegram settings...</div>;
  }

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}> Telegram Notifications</div>
      <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 16 }}>
        Get notified in your own Telegram group when a signal goes Active, Running, hits TP/SL, or is Cancelled.
      </div>

      <ol style={{ margin: "0 0 16px 0", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
        <li style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>Add <b style={{ color: "var(--text-main)" }}>@CrunchAlphaSignalBot</b> to your Telegram group and make it an admin.</li>
        <li style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>In that group, send <b style={{ color: "var(--text-main)" }}>/getchatid</b> to the bot.</li>
        <li style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>Paste the Chat ID it replies with below.</li>
      </ol>

      <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>Group Chat ID</div>
      <input type="text" placeholder="e.g. -1001234567890" value={chatId}
        onChange={e => setChatId(e.target.value)}
        style={{
          width: "100%", background: "var(--bg-elevated-soft)",
          border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)",
          padding: "10px 14px", fontSize: 13, color: "var(--text-main)",
          outline: "none", boxSizing: "border-box", marginBottom: 14,
        }} />

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }}>
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
        <span style={{ fontSize: 12, color: "var(--text-main)" }}>Enable Telegram notifications</span>
      </label>

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer" }}>
        <input type="checkbox" checked={branding} onChange={e => setBranding(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
        <span style={{ fontSize: 12, color: "var(--text-main)" }}>Show "via CrunchAlpha" footer</span>
      </label>

      {err && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 12 }}>{err}</div>}
      {msg && <div style={{ fontSize: 12, color: "var(--success)", marginBottom: 12 }}>{msg}</div>}

      <button onClick={save} disabled={saving} style={{
        padding: "10px 24px", borderRadius: "var(--radius-md)",
        border: "none", background: "var(--accent)", color: "#fff",
        fontSize: 13, fontWeight: 700, cursor: "pointer",
        opacity: saving ? 0.6 : 1,
      }}>
        {saving ? "Saving..." : "Save"}
      </button>
    </div>
  );
}

// ── Notification Channels Card (extra Telegram groups + Discord) ────────────
export function NotificationChannelsCard() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState("telegram");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    try {
      const d = await api.getNotificationChannels();
      setChannels(d.channels || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addChannel = async () => {
    setErr("");
    if (!value.trim()) {
      setErr(platform === "telegram" ? "Enter a Chat ID" : "Enter a Discord Webhook URL");
      return;
    }
    setSaving(true);
    try {
      await api.addNotificationChannel({ platform, targetValue: value.trim(), label: label.trim() });
      setValue(""); setLabel("");
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const removeChannel = async (id) => {
    if (!window.confirm("Remove this notification channel?")) return;
    try {
      await api.deleteNotificationChannel(id);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const testChannel = async (id) => {
    setTestingId(id); setErr("");
    try {
      await api.testNotificationChannel(id);
    } catch (e) { setErr(e.message); }
    finally { setTestingId(null); }
  };

  const cardStyle = {
    flex: 1, maxWidth: 420, padding: 20,
    background: "var(--bg-surface)", border: "1px solid var(--border)",
    borderRadius: "var(--radius-xl)",
  };

  if (loading) {
    return <div style={{ ...cardStyle, color: "var(--text-muted)", fontSize: 13 }}>Loading notification channels...</div>;
  }

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Additional Channels</div>
      <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 16 }}>
        Send the same notifications to extra Telegram groups and/or Discord channels, on top of the main channel above.
      </div>

      {channels.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {channels.map((ch) => (
            <div key={ch.id} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
              background: "var(--bg-elevated-soft)", border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
            }}>
              <span style={{
                fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5,
                padding: "2px 8px", borderRadius: "var(--radius-sm)",
                background: ch.platform === "discord" ? "#5865F2" : "#2AABEE", color: "#fff",
              }}>{ch.platform}</span>
              <span style={{ fontSize: 12, color: "var(--text-main)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {ch.label || ch.targetValue}
              </span>
              <button onClick={() => testChannel(ch.id)} disabled={testingId === ch.id} style={{
                fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: "var(--radius-sm)",
                border: "1px solid var(--border-subtle)", background: "transparent", color: "var(--text-muted)",
                cursor: "pointer", opacity: testingId === ch.id ? 0.6 : 1,
              }}>{testingId === ch.id ? "..." : "Test"}</button>
              <button onClick={() => removeChannel(ch.id)} style={{
                fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: "var(--radius-sm)",
                border: "1px solid var(--border-subtle)", background: "transparent", color: "var(--danger)",
                cursor: "pointer",
              }}>Remove</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <select value={platform} onChange={e => setPlatform(e.target.value)} style={{
          background: "var(--bg-elevated-soft)", border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-md)", padding: "10px 12px", fontSize: 13, color: "var(--text-main)",
          outline: "none",
        }}>
          <option value="telegram">Telegram</option>
          <option value="discord">Discord</option>
        </select>
        <input type="text" placeholder={platform === "telegram" ? "Chat ID (e.g. -1001234567890)" : "Discord Webhook URL"}
          value={value} onChange={e => setValue(e.target.value)}
          style={{
            flex: 1, background: "var(--bg-elevated-soft)", border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)", padding: "10px 14px", fontSize: 13, color: "var(--text-main)",
            outline: "none", minWidth: 0,
          }} />
      </div>

      <input type="text" placeholder="Label (optional, e.g. Binance Group)"
        value={label} onChange={e => setLabel(e.target.value)}
        style={{
          width: "100%", background: "var(--bg-elevated-soft)", border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-md)", padding: "10px 14px", fontSize: 13, color: "var(--text-main)",
          outline: "none", boxSizing: "border-box", marginBottom: 14,
        }} />

      {platform === "discord" && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5, marginBottom: 14 }}>
          Discord: Server Settings -&gt; Integrations -&gt; Webhooks -&gt; New Webhook -&gt; Copy Webhook URL. No bot invite needed.
        </div>
      )}

      {err && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 12 }}>{err}</div>}

      <button onClick={addChannel} disabled={saving} style={{
        padding: "10px 24px", borderRadius: "var(--radius-md)",
        border: "none", background: "var(--accent)", color: "#fff",
        fontSize: 13, fontWeight: 700, cursor: "pointer",
        opacity: saving ? 0.6 : 1,
      }}>
        {saving ? "Adding..." : "+ Add Channel"}
      </button>
    </div>
  );
}
